import { useEffect, useState } from "react";
import { Eye, Users, CalendarCheck, Globe } from "lucide-react";
import { api } from "@/lib/api";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-[rgba(176,132,51,0.25)] bg-surface/50 p-5 flex flex-col gap-2">
      <span className="flex items-center gap-2 text-bone/40 text-xs tracking-widest">
        <Icon size={14} className="text-gold" /> {label}
      </span>
      <span data-testid={testid} className="font-display text-4xl text-bone">
        {value}
      </span>
    </div>
  );
}

export default function AnalyticsSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/admin/analytics")
      .then((res) => setData(res.data))
      .catch(() => setData(false));
  }, []);

  if (!data) {
    return (
      <section data-testid="admin-analytics" className="border border-[rgba(176,132,51,0.25)] p-6 mb-10">
        <h2 className="font-display text-xl tracking-[0.2em] text-gold mb-4">ANALYTICS</h2>
        <p className="text-bone/40 text-sm">{data === false ? "Impossibile caricare le statistiche." : "Caricamento..."}</p>
      </section>
    );
  }

  const maxViews = Math.max(1, ...data.daily.map((d) => d.views));

  return (
    <section data-testid="admin-analytics" className="border border-[rgba(176,132,51,0.25)] p-6 mb-10">
      <h2 className="font-display text-xl tracking-[0.2em] text-gold mb-2">ANALYTICS DEL SITO</h2>
      <p className="text-bone/30 text-xs mb-6">
        Le statistiche contano da oggi in poi. I visitatori unici sono stimati in modo anonimo, senza cookie.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Eye} label="VISITE TOTALI" value={data.total_views} testid="stat-total-views" />
        <StatCard icon={Users} label="VISITATORI UNICI" value={data.unique_visitors} testid="stat-unique-visitors" />
        <StatCard icon={CalendarCheck} label="VISITE OGGI" value={data.today_views} testid="stat-today-views" />
        <StatCard icon={Users} label="UNICI OGGI" value={data.today_unique} testid="stat-today-unique" />
      </div>

      <h3 className="text-bone/50 text-xs tracking-widest mb-3">ULTIMI 14 GIORNI</h3>
      <div data-testid="analytics-chart" className="flex items-end gap-1.5 h-32 mb-8 border-b border-[rgba(176,132,51,0.2)] pb-1">
        {data.daily.length === 0 && <p className="text-bone/30 text-sm">Ancora nessuna visita registrata.</p>}
        {data.daily.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.views} visite, ${d.uniques} unici`}
            className="flex-1 bg-gold opacity-70 hover:opacity-100 transition-opacity min-h-[3px]"
            style={{ height: `${Math.max(3, (d.views / maxViews) * 100)}%` }}
          />
        ))}
      </div>

      <h3 className="text-bone/50 text-xs tracking-widest mb-3">DA DOVE ARRIVANO I VISITATORI</h3>
      {data.referrers.length === 0 ? (
        <p className="text-bone/30 text-sm">Nessun dato ancora: appariranno qui i siti che ti mandano visitatori (es. Google, Instagram).</p>
      ) : (
        <ul data-testid="analytics-referrers" className="flex flex-col gap-2">
          {data.referrers.map((r) => (
            <li key={r.ref} className="flex items-center justify-between text-sm border-b border-[rgba(176,132,51,0.1)] pb-2">
              <span className="flex items-center gap-2 text-bone/70 truncate">
                <Globe size={13} className="text-gold shrink-0" /> {r.ref}
              </span>
              <span className="font-display text-gold text-lg ml-4">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
